// ═══════════════════════════════════════════════════════════════
// GeoOps Data Sources Store
// Manages configurable camera and GIS layer sources
// Replaces hardcoded GA/HI/WA 511 camera integration
// ═══════════════════════════════════════════════════════════════

import { create } from 'zustand';
import { queryRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';

const FC = FIELDS.cameraSources;
const FG = FIELDS.gisDataSources;

// ── Types ────────────────────────────────────────────────────

export interface CameraSource {
  id: number;
  name: string;
  /** 'arcgis' | 'generic_json' | 'generic_image' */
  provider: string;
  /** URL that returns the camera list */
  listEndpoint: string;
  /** URL template for snapshot images — use {id} for camera ID */
  snapshotEndpoint: string;
  /** URL template for HLS/RTSP streams */
  streamEndpoint: string;
  apiKey: string;
  /** JSON string: { lat, lng, name, id, imageUrl, streamUrl } — field name mapping */
  fieldMapping: string;
  refreshInterval: number;
  /** Optional bounding region — if set, only cameras within radius are returned */
  regionLat: number | null;
  regionLng: number | null;
  regionRadius: number | null;
  status: string;
}

export interface GISSource {
  id: number;
  name: string;
  url: string;
  /** 'arcgis' | 'wfs' | 'geojson' */
  protocol: string;
  /** WFS layer name (e.g. "cite:roads") */
  layerName: string;
  /** JSON string: { type: 'token'|'basic'|'none', token?, username?, password? } */
  auth: string;
  refreshInterval: number;
  /** JSON string: maps feature attribute values → POI category name */
  categoryMapping: string;
  status: string;
  lastSynced: string;
  featureCount: number;
}

// ── Field mappers ────────────────────────────────────────────

function mapCamera(r: Record<string, unknown>): CameraSource {
  return {
    id: r.record_id as number,
    name: (r[FC.name] as string) || '',
    provider: (r[FC.provider] as string) || 'generic_json',
    listEndpoint: (r[FC.listEndpoint] as string) || (r[FC.baseUrl as keyof typeof FC] as string) || '',
    snapshotEndpoint: (r[FC.snapshotEndpoint] as string) || '',
    streamEndpoint: (r[FC.streamEndpoint] as string) || '',
    apiKey: (r[FC.apiKey] as string) || '',
    fieldMapping: (r[FC.fieldMapping] as string) || '{}',
    refreshInterval: Number(r[FC.refreshInterval]) || 30,
    regionLat: r[FC.regionLat] != null ? Number(r[FC.regionLat]) : null,
    regionLng: r[FC.regionLng] != null ? Number(r[FC.regionLng]) : null,
    regionRadius: r[FC.regionRadius] != null ? Number(r[FC.regionRadius]) : null,
    status: (r[FC.status] as string) || 'active',
  };
}

function mapGIS(r: Record<string, unknown>): GISSource {
  return {
    id: r.record_id as number,
    name: (r[FG.name] as string) || '',
    url: (r[FG.url] as string) || '',
    protocol: (r[FG.protocol] as string) || 'arcgis',
    layerName: (r[FG.layerName] as string) || '',
    auth: (r[FG.auth] as string) || '{}',
    refreshInterval: Number(r[FG.refreshInterval]) || 60,
    categoryMapping: (r[FG.categoryMapping] as string) || '{}',
    status: (r[FG.status] as string) || 'active',
    lastSynced: (r[FG.lastSynced] as string) || '',
    featureCount: Number(r[FG.featureCount]) || 0,
  };
}

// ── Store ────────────────────────────────────────────────────

interface GeoOpsDataState {
  cameras: CameraSource[];
  gisLayers: GISSource[];
  loading: boolean;

  loadCameras: () => Promise<void>;
  createCamera: (data: Omit<CameraSource, 'id'>) => Promise<void>;
  updateCamera: (id: number, data: Partial<CameraSource>) => Promise<void>;
  deleteCamera: (id: number) => Promise<void>;

  loadGISLayers: () => Promise<void>;
  createGISLayer: (data: Omit<GISSource, 'id'>) => Promise<void>;
  updateGISLayer: (id: number, data: Partial<GISSource>) => Promise<void>;
  deleteGISLayer: (id: number) => Promise<void>;
}

export const useGeoOpsDataStore = create<GeoOpsDataState>((set, get) => ({
  cameras: [],
  gisLayers: [],
  loading: false,

  // ── Camera Sources ──────────────────────────────────────

  loadCameras: async () => {
    set({ loading: true });
    try {
      const res = await queryRecords<Record<string, unknown>>(TABLES.cameraSources, {
        limit: 200, sort: { field: 'created_at', direction: 'asc' },
      });
      set({ cameras: res.records.map(mapCamera) });
    } catch (e) { console.error('[GeoOpsData] loadCameras', e); }
    set({ loading: false });
  },

  createCamera: async (data) => {
    try {
      await createRecord(TABLES.cameraSources, {
        [FC.name]: data.name,
        [FC.provider]: data.provider,
        [FC.listEndpoint]: data.listEndpoint,
        [FC.snapshotEndpoint]: data.snapshotEndpoint,
        [FC.streamEndpoint]: data.streamEndpoint,
        [FC.apiKey]: data.apiKey,
        [FC.fieldMapping]: data.fieldMapping,
        [FC.refreshInterval]: data.refreshInterval,
        ...(data.regionLat != null ? { [FC.regionLat]: data.regionLat } : {}),
        ...(data.regionLng != null ? { [FC.regionLng]: data.regionLng } : {}),
        ...(data.regionRadius != null ? { [FC.regionRadius]: data.regionRadius } : {}),
        [FC.status]: data.status || 'active',
      });
      await get().loadCameras();
    } catch (e) { console.error('[GeoOpsData] createCamera', e); throw e; }
  },

  updateCamera: async (id, data) => {
    try {
      const payload: Record<string, unknown> = {};
      if (data.name !== undefined) payload[FC.name] = data.name;
      if (data.provider !== undefined) payload[FC.provider] = data.provider;
      if (data.listEndpoint !== undefined) payload[FC.listEndpoint] = data.listEndpoint;
      if (data.snapshotEndpoint !== undefined) payload[FC.snapshotEndpoint] = data.snapshotEndpoint;
      if (data.streamEndpoint !== undefined) payload[FC.streamEndpoint] = data.streamEndpoint;
      if (data.apiKey !== undefined) payload[FC.apiKey] = data.apiKey;
      if (data.fieldMapping !== undefined) payload[FC.fieldMapping] = data.fieldMapping;
      if (data.refreshInterval !== undefined) payload[FC.refreshInterval] = data.refreshInterval;
      if (data.regionLat !== undefined) payload[FC.regionLat] = data.regionLat;
      if (data.regionLng !== undefined) payload[FC.regionLng] = data.regionLng;
      if (data.regionRadius !== undefined) payload[FC.regionRadius] = data.regionRadius;
      if (data.status !== undefined) payload[FC.status] = data.status;
      await updateRecord(TABLES.cameraSources, id, payload);
      await get().loadCameras();
    } catch (e) { console.error('[GeoOpsData] updateCamera', e); throw e; }
  },

  deleteCamera: async (id) => {
    try {
      await deleteRecord(TABLES.cameraSources, id);
      set({ cameras: get().cameras.filter(c => c.id !== id) });
    } catch (e) { console.error('[GeoOpsData] deleteCamera', e); throw e; }
  },

  // ── GIS Layers ──────────────────────────────────────────

  loadGISLayers: async () => {
    set({ loading: true });
    try {
      const res = await queryRecords<Record<string, unknown>>(TABLES.gisDataSources, {
        limit: 200, sort: { field: 'created_at', direction: 'asc' },
      });
      set({ gisLayers: res.records.map(mapGIS) });
    } catch (e) { console.error('[GeoOpsData] loadGISLayers', e); }
    set({ loading: false });
  },

  createGISLayer: async (data) => {
    try {
      await createRecord(TABLES.gisDataSources, {
        [FG.name]: data.name,
        [FG.url]: data.url,
        [FG.protocol]: data.protocol,
        [FG.layerName]: data.layerName,
        [FG.auth]: data.auth || '{}',
        [FG.refreshInterval]: data.refreshInterval,
        [FG.categoryMapping]: data.categoryMapping || '{}',
        [FG.status]: data.status || 'active',
      });
      await get().loadGISLayers();
    } catch (e) { console.error('[GeoOpsData] createGISLayer', e); throw e; }
  },

  updateGISLayer: async (id, data) => {
    try {
      const payload: Record<string, unknown> = {};
      if (data.name !== undefined) payload[FG.name] = data.name;
      if (data.url !== undefined) payload[FG.url] = data.url;
      if (data.protocol !== undefined) payload[FG.protocol] = data.protocol;
      if (data.layerName !== undefined) payload[FG.layerName] = data.layerName;
      if (data.auth !== undefined) payload[FG.auth] = data.auth;
      if (data.refreshInterval !== undefined) payload[FG.refreshInterval] = data.refreshInterval;
      if (data.categoryMapping !== undefined) payload[FG.categoryMapping] = data.categoryMapping;
      if (data.status !== undefined) payload[FG.status] = data.status;
      await updateRecord(TABLES.gisDataSources, id, payload);
      await get().loadGISLayers();
    } catch (e) { console.error('[GeoOpsData] updateGISLayer', e); throw e; }
  },

  deleteGISLayer: async (id) => {
    try {
      await deleteRecord(TABLES.gisDataSources, id);
      set({ gisLayers: get().gisLayers.filter(g => g.id !== id) });
    } catch (e) { console.error('[GeoOpsData] deleteGISLayer', e); throw e; }
  },
}));
